import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  makeStyles,
  Link as MuiLink,
  Paper,
  Popper,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { useThemeMode } from '@src/contexts/ThemeMode';
import { updateUserState } from '@src/reducers/userState';
import { selectAuthenticationInfo, userState } from '@src/selectors/user';
import { LoginSuccess } from '@src/types';
import { RefObject, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ThemeModeToggle from './ThemeModeToggle';
import UserAvatar from './UserAvatar';

const useStyles = makeStyles(theme => ({
  spacing: { display: 'flex', gap: '8px', alignItems: 'center' },
  agentNameEmployerNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    width: '160px',
  },
  agentName: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    lineHeight: '19px',
    color: theme.palette.text.primary,
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  employerName: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    lineHeight: '12px',
    color: theme.palette.primary.main,
    fontSize: '12px',
    '& .MuiLink-root, & a': {
      color: 'inherit',
    },
  },
}));

export const AgentNameItem: React.FC<{ user: LoginSuccess }> = ({ user }) => {
  const { agentNameEmployerNameContainer, agentName, employerName } =
    useStyles();

  const [displayTooltip, setDisplayTooltip] = useState(false);

  const fullEmployerName = `${user?.employer?.name}`;

  return (
    <Box className={agentNameEmployerNameContainer}>
      <Typography className={agentName}>{user.fullName}</Typography>
      <Typography className={employerName}>
        <Tooltip
          title={fullEmployerName}
          open={displayTooltip}
          onOpen={() => {
            if (fullEmployerName.length > 20) setDisplayTooltip(true);
          }}
          onClose={() => setDisplayTooltip(false)}
        >
          <MuiLink
            color="inherit"
            href="/admin/myaccount"
            onClick={event => event.stopPropagation()}
            style={{
              display: 'block',
              width: 'fit-content',
              maxWidth: '160px',
            }}
          >
            <Box
              // component="span"
              style={{
                width: 'fit-content',
                maxWidth: '160px',
                height: '12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '10px',
              }}
            >
              {fullEmployerName}
            </Box>
          </MuiLink>
        </Tooltip>
      </Typography>
    </Box>
  );
};

const ThemeModeRow: React.FC = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{ minHeight: 40 }}
    >
      <Box>
        <Typography
          variant="body2"
          noWrap
          style={{ fontWeight: 600, fontSize: '13px' }}
        >
          <FormattedMessage
            id={isDark ? 'Header.theme.dark' : 'Header.theme.light'}
          />
        </Typography>
      </Box>
      <Box mt={-0.8}>
        <ThemeModeToggle />
      </Box>
    </Box>
  );
};

export const UserStateMenu: React.FC<{
  userBtn: RefObject<HTMLButtonElement>;
  openUserState: boolean;
  state: boolean;
}> = ({ userBtn, openUserState, state }) => {
  const dispatch = useDispatch();
  const { popoverSurface } = useTheme();
  return (
    <Popper
      open={openUserState}
      anchorEl={userBtn.current}
      style={{ zIndex: 9999, marginTop: '12px' }}
    >
      <Box
        component={Paper}
        py={1.5}
        px={2}
        display="flex"
        flexDirection="column"
        gridGap={6}
        style={{
          minWidth: 200,
          backgroundColor: popoverSurface.background,
          border: popoverSurface.border,
          boxShadow: popoverSurface.shadow,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ minHeight: 40 }}
        >
          <Box>
            <Typography
              variant="body2"
              style={{ fontWeight: 600, fontSize: '13px' }}
            >
              <FormattedMessage id="AvatarWithStatusMenu.Status.Message" />
            </Typography>
            <Typography
              variant="caption"
              style={{ fontSize: '11px', lineHeight: '14px', display: 'block' }}
              color="textSecondary"
            >
              <FormattedMessage
                id={`AvatarWithStatusMenu.Status.${
                  state ? 'online' : 'offline'
                }`}
              />
            </Typography>
          </Box>
          <Box mt={-0.5}>
            <Switch
              checked={state}
              onChange={(_, isCheked) =>
                dispatch(updateUserState({ online: isCheked }))
              }
              size="small"
              color="primary"
            />
          </Box>
        </Box>
        <ThemeModeRow />
        <Divider />
        <Link to="/admin/profile-settings" style={{ marginTop: 4 }}>
          <Typography
            variant="body2"
            color="primary"
            style={{ fontWeight: 500, fontSize: '13px' }}
          >
            <FormattedMessage id="ProfileAndSettings" />
          </Typography>
        </Link>
      </Box>
    </Popper>
  );
};

export const AvatarWithStatusMenu: React.FC = () => {
  const userBtn = useRef<HTMLButtonElement>(null);
  const [openUserState, setOpenUserState] = useState(false);

  const { spacing } = useStyles();

  const state = useSelector(userState);
  const user: LoginSuccess = useSelector(selectAuthenticationInfo);

  return (
    <ClickAwayListener onClickAway={() => setOpenUserState(false)}>
      <span>
        <Box display="flex" height="100%">
          <ButtonBase
            ref={userBtn}
            onClick={() => setOpenUserState(!openUserState)}
            className={spacing}
          >
            <Box position="relative">
              <UserAvatar imageUrl={user.avatarUrl} isOnline={state} />
            </Box>
            <AgentNameItem user={user} />
          </ButtonBase>
        </Box>

        <UserStateMenu
          userBtn={userBtn}
          openUserState={openUserState}
          state={!!state}
        />
      </span>
    </ClickAwayListener>
  );
};
